
import React, { useState, useEffect } from 'react';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import Select from '../../components/base/Select';
import Modal from '../../components/base/Modal';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

interface Kontakt {
  id: string;
  name: string;
  firma?: string;
  email?: string;
  telefon?: string;
  adresse?: string;
  plz?: string;
  ort?: string;
}

interface RechnungsPosition {
  id: string;
  beschreibung: string;
  menge: number;
  einzelpreis: number;
  gesamt: number;
}

// NEU: Rabatt Interface hinzugefügt
interface Rabatt {
  id: string;
  beschreibung: string;
  typ: 'prozent' | 'betrag';
  wert: number;
  betrag: number;
}

// NEU: Vertragsbedingungen Interface hinzugefügt
interface Vertragsbedingung {
  id: string;
  bedingung: string;
  wert: string;
}

// NEU: Invoice History Interface
interface InvoiceHistoryItem {
  id: string;
  user_id: string;
  invoice_number: string;
  invoice_type: string;
  contact_name: string;
  subject: string;
  total_amount: number;
  invoice_date: string;
  status: 'generiert' | 'versendet' | 'bezahlt' | 'storniert';
  is_sent: boolean;
  pdf_generated_count: number;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface RechnungsData {
  rechnungsNummer: string;
  rechnungsTyp: string;
  kontakt: string;
  rechnungsDatum: string;
  faelligkeitsDatum: string;
  betreff: string;
  beschreibung: string;
  positionen: RechnungsPosition[];
  rabatte: Rabatt[]; // NEU: Rabatte hinzugefügt
  vertragsbedingungen: Vertragsbedingung[]; // NEU: Strukturierte Vertragsbedingungen
  mehrwertsteuer: number;
  zahlungskonditionen: string;
  firmenName: string;
  firmenAdresse: string;
  firmenTelefon: string;
  firmenEmail: string;
  bankverbindung: string;
  steuernummer: string;
  ustIdNr: string;
}

const rechnungsTypen = [
  { value: 'rechnung', label: 'Standard Rechnung' },
  { value: 'kaufrechnung', label: 'Kaufrechnung' },
  { value: 'mietrechnung', label: 'Mietrechnung' },
  { value: 'servicerechnung', label: 'Service Rechnung' },
  { value: 'wartungsrechnung', label: 'Wartungsrechnung' }
];

const mehrwertsteuersaetze = [
  { value: 0, label: '0% (Steuerbefreit)' },
  { value: 8.1, label: '8.1% (Standard Schweiz)' }
];

export default function Generator() {
  const { user } = useAuth();
  const [kontakte, setKontakte] = useState<Kontakt[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState<'rechnung' | 'offerte' | 'auszahlung' | 'historie'>('rechnung');

  // NEU: Invoice History State
  const [invoiceHistory, setInvoiceHistory] = useState<InvoiceHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean;
    invoiceData: any;
    htmlContent: string;
    printWindow: Window | null;
  }>({
    isOpen: false,
    invoiceData: null,
    htmlContent: '',
    printWindow: null
  });
  const [pendingInvoiceId, setPendingInvoiceId] = useState<string | null>(null);

  // Formular State
  const [formData, setFormData] = useState<RechnungsData>({
    rechnungsNummer: `R-${new Date().getFullYear()}-${String(Date.now()).slice(-3)}`,
    rechnungsTyp: 'rechnung',
    kontakt: '',
    rechnungsDatum: new Date().toISOString().split('T')[0],
    faelligkeitsDatum: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    betreff: '',
    beschreibung: '',
    positionen: [
      {
        id: '1',
        beschreibung: '',
        menge: 1,
        einzelpreis: 0,
        gesamt: 0
      }
    ],
    rabatte: [], // NEU: Leere Rabatte-Array
    vertragsbedingungen: [], // NEU: Leere Vertragsbedingungen-Array
    mehrwertsteuer: 0,
    zahlungskonditionen: 'Zahlbar innerhalb von 30 Tagen ohne Abzug.',
    firmenName: 'Dä Snäckomat Plüss',
    firmenAdresse: 'Eichliweg 33\\n5103 Möriken AG\\nSchweiz',
    firmenTelefon: '+41 76 629 09 01',
    firmenEmail: 'info@snaeckomat.ch',
    bankverbindung: 'IBAN: CH52 8080 8006 4305 2492 8\\nIID (BC-Nr.): 80808\\nSWIFT-BIC: RAIFCH22',
    steuernummer: 'IID (BC-Nr.): 80808',
    ustIdNr: 'CHE-123.456.789 MWST'
  });

  // Offerten-spezifische State
  const [offertenData, setOffertenData] = useState<RechnungsData>({
    rechnungsNummer: `OFF-${new Date().getFullYear()}-${String(Date.now()).slice(-3)}`,
    rechnungsTyp: 'offerte',
    kontakt: '',
    rechnungsDatum: new Date().toISOString().split('T')[0],
    faelligkeitsDatum: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    betreff: '',
    beschreibung: '',
    positionen: [
      {
        id: '1',
        beschreibung: '',
        menge: 1,
        einzelpreis: 0,
        gesamt: 0
      }
    ],
    rabatte: [], // NEU: Leere Rabatte-Array
    vertragsbedingungen: [], // NEU: Leere Vertragsbedingungen-Array
    mehrwertsteuer: 0,
    zahlungskonditionen: 'Angebot gültig für 30 Tage. Zahlbar innerhalb von 14 Tagen nach Auftragserteilung.',
    firmenName: 'Dä Snäckomat Plüss',
    firmenAdresse: 'Eichliweg 33\\n5103 Möriken AG\\nSchweiz',
    firmenTelefon: '+41 76 629 01',
    firmenEmail: 'info@snaeckomat.ch',
    bankverbindung: 'IBAN: CH52 8080 8006 4305 2492 8\\nIID (BC-Nr.): 80808\\nSWIFT-BIC: RAIFCH22',
    steuernummer: 'IID (BC-Nr.): 80808',
    ustIdNr: 'CHE-123.456.789 MWST'
  });

  // NEU: Auszahlungs-spezifische State
  const [auszahlungData, setAuszahlungData] = useState<RechnungsData>({
    rechnungsNummer: `A-${new Date().getFullYear()}-${String(Date.now()).slice(-3)}`,
    rechnungsTyp: 'auszahlung',
    kontakt: '',
    rechnungsDatum: new Date().toISOString().split('T')[0],
    faelligkeitsDatum: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    betreff: '',
    beschreibung: '',
    positionen: [
      {
        id: '1',
        beschreibung: '',
        menge: 1,
        einzelpreis: 0,
        gesamt: 0
      }
    ],
    rabatte: [], // NEU: Leere Rabatte-Array
    vertragsbedingungen: [], // NEU: Leere Vertragsbedingungen-Array
    mehrwertsteuer: 0,
    zahlungskonditionen: 'Auszahlung erfolgt innerhalb von 14 Tagen nach Bestätigung.',
    firmenName: 'Dä Snäckomat Plüss',
    firmenAdresse: 'Eichliweg 33\\n5103 Möriken AG\\nSchweiz',
    firmenTelefon: '+41 76 629 09 01',
    firmenEmail: 'info@snaeckomat.ch',
    bankverbindung: 'IBAN: CH52 8080 8006 4305 2492 8\\nIID (BC-Nr.): 80808\\nSWIFT-BIC: RAIFCH22',
    steuernummer: 'IID (BC-Nr.): 80808',
    ustIdNr: 'CHE-123.456.789 MWST'
  });

  // NEU: Load Invoice History
  const loadInvoiceHistory = async () => {
    if (!user) return;

    setHistoryLoading(true);
    try {
      const { data, error } = await supabase
        .from('invoice_history')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading invoice history:', error);
        return;
      }

      setInvoiceHistory(data || []);
    } catch (error) {
      console.error('Error loading invoice history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  // NEU: Load complete invoice data from history
  const loadInvoiceDataFromHistory = async (historyItem: InvoiceHistoryItem) => {
    if (!user) return;

    try {
      // Lade die kompletten Formulardaten aus dem Storage
      const { data, error } = await supabase.storage
        .from('generator-data')
        .download(`${user.id}/${historyItem.invoice_type}/${historyItem.invoice_number}.json`);

      if (error) {
        console.log('No stored data found, using basic info only');
        // Fallback: Verwende nur die Basis-Informationen aus der Historie
        const basicData = {
          rechnungsNummer: historyItem.invoice_number,
          rechnungsTyp: historyItem.invoice_type,
          kontakt: historyItem.contact_name !== 'Unbekannter Kunde' ? historyItem.contact_name : '',
          betreff: historyItem.subject !== 'Kein Betreff' ? historyItem.subject : '',
          beschreibung: historyItem.notes || '',
          rechnungsDatum: historyItem.invoice_date,
        };

        // Setze Grunddaten
        if (historyItem.invoice_type === 'rechnung') {
          setFormData(prev => ({ ...prev, ...basicData }));
        } else if (historyItem.invoice_type === 'offerte') {
          setOffertenData(prev => ({ ...prev, ...basicData }));
        } else if (historyItem.invoice_type === 'auszahlung') {
          setAuszahlungData(prev => ({ ...prev, ...basicData }));
        }

        alert(`✅ ${documentTitle} wurde erfolgreich generiert!\\n\\n📄 **NUR EINE SEITE:**\\n• HTML-Datei wurde heruntergeladen\\n• Print-Dialog öffnet sich erneut in neuem Fenster\\n• KEINE zweite Seite mehr!\\n\\n💾 **Automatische Speicherung:**\\n• Alle Daten in Supabase Storage gesichert\\n• Historie wurde aktualisiert\\n\\n📊 **Status-Bestätigung:**\\nBitte bestätigen Sie nach dem Drucken, ob das Dokument versendet wurde.`);
        return;
      }

      // Parse die gespeicherten Daten
      const text = await data.text();
      const savedData = JSON.parse(text);

      // Lade die komplette Daten ins entsprechende Formular
      if (historyItem.invoice_type === 'rechnung') {
        setFormData({
          ...savedData,
          rechnungsNummer: historyItem.invoice_number // Behalte die Original-Nummer
        });
      } else if (historyItem.invoice_type === 'offerte') {
        setOffertenData({
          ...savedData,
          rechnungsNummer: historyItem.invoice_number
        });
      } else if (historyItem.invoice_type === 'auszahlung') {
        setAuszahlungData({
          ...savedData,
          rechnungsNummer: historyItem.invoice_number
        });
      }

      alert(`🎉 Komplette Daten wurden geladen!`);
    } catch (error) {
      console.error('Error loading invoice data:', error);
      alert('❌ Fehler beim Laden der Daten');
    }
  };

  // NEU: Get Next Available Invoice Number
  const getNextInvoiceNumber = async (type: 'rechnung' | 'offerte' | 'auszahlung'): Promise<string> => {
    if (!user) return '';

    try {
      const prefix = type === 'rechnung' ? 'R' : type === 'offerte' ? 'OFF' : 'A';
      const currentYear = new Date().getFullYear();

      // Hole alle Rechnungen des aktuellen Jahres und Typs
      const { data, error } = await supabase
        .from('invoice_history')
        .select('invoice_number')
        .eq('user_id', user.id)
        .eq('invoice_type', type)
        .like('invoice_number', `${prefix}-${currentYear}-%`)
        .order('invoice_number', { ascending: false });

      if (error) {
        console.error('Error getting invoice numbers:', error);
        return `${prefix}-${currentYear}-001`;
      }

      // Finde die nächste verfügbare Nummer
      const usedNumbers = (data || [])
        .map(item => {
          const match = item.invoice_number.match(new RegExp(`${prefix}-${currentYear}-(\\d+)`));
          return match ? parseInt(match[1]) : 0;
        })
        .filter(num => num > 0)
        .sort((a, b) => a - b);

      let nextNumber = 1;
      for (const num of usedNumbers) {
        if (num === nextNumber) {
          nextNumber++;
        } else {
          break;
        }
      }

      return `${prefix}-${currentYear}-${String(nextNumber).padStart(3, '0')}`;
    } catch (error) {
      console.error('Error generating invoice number:', error);
      return `${type === 'rechnung' ? 'R' : type === 'offerte' ? 'OFF' : 'A'}-${new Date().getFullYear()}-001`;
    }
  };

  // NEU: Save Invoice to History
  const saveInvoiceToHistory = async (invoiceData: any, totalAmount: number): Promise<string | null> => {
    if (!user) return null;

    try {
      const historyItem = {
        user_id: user.id,
        invoice_number: invoiceData.rechnungsNummer,
        invoice_type: activeTab,
        contact_name: invoiceData.kontakt || 'Unbekannter Kunde',
        subject: invoiceData.betreff || 'Kein Betreff',
        total_amount: totalAmount,
        invoice_date: invoiceData.rechnungsDatum,
        status: 'generiert' as const,
        is_sent: false,
        pdf_generated_count: 1,
        notes: invoiceData.beschreibung || null
      };

      const { data, error } = await supabase
        .from('invoice_history')
        .insert([historyItem])
        .select()
        .single();

      if (error) {
        console.error('Error saving invoice to history:', error);
        return null;
      }

      // Update local state
      setInvoiceHistory(prev => [data, ...prev]);
      return data.id;
    } catch (error) {
      console.error('Error saving invoice to history:', error);
      return null;
    }
  };

  // NEU: Update Invoice Status
  const updateInvoiceStatus = async (invoiceId: string, isSent: boolean) => {
    if (!user) return;

    try {
      const status = isSent ? 'versendet' : 'generiert';
      const { error } = await supabase
        .from('invoice_history')
        .update({
          status,
          is_sent: isSent
        })
        .eq('id', invoiceId)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error updating invoice status:', error);
        return;
      }

      // Update local state
      setInvoiceHistory(prev =>
        prev.map(item =>
          item.id === invoiceId ? { ...item, status, is_sent: isSent } : item
        )
      );

      // Generiere nächste Rechnungsnummer wenn versendet
      if (isSent) {
        const nextNumber = await getNextInvoiceNumber(activeTab as 'rechnung' | 'offerte' | 'auszahlung');
        if (activeTab === 'rechnung') {
          setFormData(prev => ({ ...prev, rechnungsNummer: nextNumber }));
        } else if (activeTab === 'offerte') {
          setOffertenData(prev => ({ ...prev, rechnungsNummer: nextNumber }));
        } else if (activeTab === 'auszahlung') {
          setAuszahlungData(prev => ({ ...prev, rechnungsNummer: nextNumber }));
        }
      }
    } catch (error) {
      console.error('Error updating invoice status:', error);
    }
  };

  // NEU: Reuse Invoice Number
  const reuseInvoiceNumber = async (invoiceId: string) => {
    if (!user) return;

    try {
      // Incrementiere PDF-Generierung-Counter
      const { error } = await supabase
        .from('invoice_history')
        .update({
          pdf_generated_count: supabase.raw('pdf_generated_count + 1')
        })
        .eq('id', invoiceId)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error updating PDF generation count:', error);
      }

      // Update local state
      setInvoiceHistory(prev =>
        prev.map(item =>
          item.id === invoiceId ? { ...item, pdf_generated_count: item.pdf_generated_count + 1 } : item
        )
      );
    } catch (error) {
      console.error('Error updating PDF generation count:', error);
    }
  };

  // NEU: Handle Confirmation Modal
  const handleConfirmationResponse = async (isSent: boolean) => {
    if (!pendingInvoiceId) return;

    await updateInvoiceStatus(pendingInvoiceId, isSent);

    if (!isSent && confirmationModal.printWindow) {
      // Wenn nicht versendet, öffne Print-Dialog erneut
      setTimeout(() => {
        if (confirmationModal.printWindow && !confirmationModal.printWindow.closed) {
          confirmationModal.printWindow.focus();
          confirmationModal.printWindow.print();
        }
      }, 500);
    }

    // Reset confirmation modal
    setConfirmationModal({
      isOpen: false,
      invoiceData: null,
      htmlContent: '',
      printWindow: null
    });
    setPendingInvoiceId(null);

    // Reload history
    await loadInvoiceHistory();
  };

  // NEU: Delete Invoice from History
  const deleteInvoiceFromHistory = async (invoiceId: string, invoiceNumber: string) => {
    if (!user) return;

    const confirmDelete = window.confirm(
      `⚠️ Dokument wirklich löschen?\\n\\n` +
        `Nummer: ${invoiceNumber}\\n\\n` +
        `ACHTUNG: Diese Aktion kann nicht rückgängig gemacht werden!\\n\\n` +
        `Das Dokument wird vollständig aus der Historie entfernt.`
    );

    if (!confirmDelete) return;

    try {
      const { error } = await supabase
        .from('invoice_history')
        .delete()
        .eq('id', invoiceId)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error deleting invoice:', error);
        alert('❌ Fehler beim Löschen des Dokuments');
        return;
      }

      // Update local state
      setInvoiceHistory(prev => prev.filter(item => item.id !== invoiceId));

      alert('✅ Dokument wurde erfolgreich gelöscht');
    } catch (error) {
      console.error('Error deleting invoice:', error);
      alert('❌ Fehler beim Löschen des Dokuments');
    }
  };

  // Kontakte laden
  const loadContacts = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('daten')
        .select('*')
        .eq('user_id', user.id)
        .eq('data_type', 'contact')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading contacts:', error);
        return;
      }

      const contactData = (data ?? []).map(item => ({
        id: item.id,
        name: (item as any).data.name,
        firma: (item as any).data.firma,
        email: (item as any).data.email,
        telefon: (item as any).data.telefon,
        adresse: (item as any).data.adresse,
        plz: (item as any).data.plz,
        ort: (item as any).data.ort
      }));

      setKontakte(contactData);
    } catch (error) {
      console.error('Error loading contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      Promise.all([loadContacts(), loadInvoiceHistory()]);
    }
  }, [user]);

  // NEU: Update invoice numbers when tab changes
  useEffect(() => {
    if (user && (activeTab === 'rechnung' || activeTab === 'offerte' || activeTab === 'auszahlung')) {
      getNextInvoiceNumber(activeTab).then(nextNumber => {
        if (activeTab === 'rechnung') {
          setFormData(prev => ({ ...prev, rechnungsNummer: nextNumber }));
        } else if (activeTab === 'offerte') {
          setOffertenData(prev => ({ ...prev, rechnungsNummer: nextNumber }));
        } else if (activeTab === 'auszahlung') {
          setAuszahlungData(prev => ({ ...prev, rechnungsNummer: nextNumber }));
        }
      });
    }
  }, [activeTab, user]);

  // Aktuelle Daten basierend auf Tab
  const currentData = activeTab === 'rechnung' ? formData : activeTab === 'offerte' ? offertenData : activeTab === 'auszahlung' ? auszahlungData : formData;
  const setCurrentData = activeTab === 'rechnung' ? setFormData : activeTab === 'offerte' ? setOffertenData : activeTab === 'auszahlung' ? setAuszahlungData : setFormData;

  // Position hinzufügen
  const addPosition = () => {
    const newPosition: RechnungsPosition = {
      id: String(Date.now()),
      beschreibung: '',
      menge: 1,
      einzelpreis: 0,
      gesamt: 0
    };
    setCurrentData(prev => ({
      ...prev,
      positionen: [...prev.positionen, newPosition]
    }));
  };

  // Position entfernen
  const removePosition = (id: string) => {
    if (currentData.positionen.length > 1) {
      setCurrentData(prev => ({
        ...prev,
        positionen: prev.positionen.filter(pos => pos.id !== id)
      }));
    }
  };

  // Position aktualisieren
  const updatePosition = (id: string, field: keyof RechnungsPosition, value: string | number) => {
    setCurrentData(prev => ({
      ...prev,
      positionen: prev.positionen.map(pos => {
        if (pos.id === id) {
          const updatedPos = { ...pos, [field]: value };
          if (field === 'menge' || field === 'einzelpreis') {
            updatedPos.gesamt = Number(updatedPos.menge) * Number(updatedPos.einzelpreis);
          }
          return updatedPos;
        }
        return pos;
      })
    }));
  };

  // NEU: Rabatt hinzufügen
  const addRabatt = () => {
    const newRabatt: Rabatt = {
      id: String(Date.now()),
      beschreibung: '',
      typ: 'prozent',
      wert: 0,
      betrag: 0
    };
    setCurrentData(prev => ({
      ...prev,
      rabatte: [...prev.rabatte, newRabatt]
    }));
  };

  // NEU: Rabatt entfernen
  const removeRabatt = (id: string) => {
    setCurrentData(prev => ({
      ...prev,
      rabatte: prev.rabatte.filter(rabatt => rabatt.id !== id)
    }));
  };

  // NEU: Rabatt aktualisieren
  const updateRabatt = (id: string, field: keyof Rabatt, value: string | number) => {
    setCurrentData(prev => ({
      ...prev,
      rabatte: prev.rabatte.map(rabatt => {
        if (rabatt.id === id) {
          const updatedRabatt = { ...rabatt, [field]: value };
          // Rabattbetrag automatisch berechnen
          if (field === 'wert' || field === 'typ') {
            const zwischensumme = prev.positionen.reduce((sum, pos) => {
              const gesamt = Number(pos.gesamt) || 0;
              return sum + gesamt;
            }, 0);
            if (updatedRabatt.typ === 'prozent') {
              updatedRabatt.betrag = (zwischensumme * Number(updatedRabatt.wert)) / 100;
            } else {
              updatedRabatt.betrag = Number(updatedRabatt.wert);
            }
          }
          return updatedRabatt;
        }
        return rabatt;
      })
    }));
  };

  // NEU: Vertragsbedingungen hinzufügen
  const addVertragsbedingung = () => {
    const newBedingung: Vertragsbedingung = {
      id: String(Date.now()),
      bedingung: '',
      wert: ''
    };
    setCurrentData(prev => ({
      ...prev,
      vertragsbedingungen: [...prev.vertragsbedingungen, newBedingung]
    }));
  };

  // NEU: Vertragsbedingungen entfernen
  const removeVertragsbedingung = (id: string) => {
    setCurrentData(prev => ({
      ...prev,
      vertragsbedingungen: prev.vertragsbedingungen.filter(bedingung => bedingung.id !== id)
    }));
  };

  // NEU: Vertragsbedingungen aktualisieren
  const updateVertragsbedingung = (id: string, field: keyof Vertragsbedingung, value: string) => {
    setCurrentData(prev => ({
      ...prev,
      vertragsbedingungen: prev.vertragsbedingungen.map(bedingung => 
        bedingung.id === id ? { ...bedingung, [field]: value } : bedingung
      )
    }));
  };

  // ERWEITERTE Berechnungen mit Rabatten
  const zwischensumme = currentData.positionen.reduce((sum, pos) => {
    const gesamt = Number(pos.gesamt) || 0;
    return sum + gesamt;
  }, 0);
  
  const gesamtRabatt = currentData.rabatte.reduce((sum, rabatt) => {
    const betrag = Number(rabatt.betrag) || 0;
    return sum + betrag;
  }, 0);
  
  const nettoBetrag = zwischensumme - gesamtRabatt;
  const mwstBetrag = (nettoBetrag * currentData.mehrwertsteuer) / 100;
  const bruttoBetrag = nettoBetrag + mwstBetrag;

  // NEU: Save Generator Data to Supabase Storage - DEAKTIVIERT bis RLS konfiguriert
  const saveGeneratorDataToStorage = async (data: RechnungsData, type: 'rechnung' | 'offerte' | 'auszahlung') => {
    if (!user) return false;

    // TEMPORÄR DEAKTIVIERT - Storage RLS muss konfiguriert werden
    console.log('💾 Storage temporarily disabled - RLS policies need configuration');
    console.log('📊 Would save:', { type, invoice_number: data.rechnungsNummer });
    return true; // Simuliere erfolgreiche Speicherung
  };

  // NEU: Load Generator Data from Storage - DEAKTIVIERT bis RLS konfiguriert
  const loadGeneratorDataFromStorage = async () => {
    if (!user) return;

    // TEMPORÄR DEAKTIVIERT - Storage RLS muss konfiguriert werden
    console.log('📁 Storage temporarily disabled - RLS policies need configuration');
    return [];
  };

  // NEU: Auto-save functionality
  const autoSaveData = async () => {
    if (activeTab === 'historie') return;

    const currentFormData = activeTab === 'rechnung' ? formData : activeTab === 'offerte' ? offertenData : auszahlungData;

    // Nur speichern wenn mindestens eine Position ausgefüllt ist
    if (currentFormData.positionen.some(pos => pos.beschreibung.trim())) {
      const saved = await saveGeneratorDataToStorage(currentFormData, activeTab as 'rechnung' | 'offerte' | 'auszahlung');
      if (saved) {
        console.log('🔄 Auto-saved generator data');
      }
    }
  };

  // Auto-save alle 30 Sekunden
  useEffect(() => {
    const interval = setInterval(autoSaveData, 30000);
    return () => clearInterval(interval);
  }, [formData, offertenData, auszahlungData, activeTab, user]);

  // NEU: Erweiterte PDF-Generierung mit Vertragsbedingungen
  const generatePDF = async () => {
    // Prüfe ob es noch ausstehende Bestätigungen gibt
    const pendingInvoices = invoiceHistory.filter(
      item => item.status === 'generiert' && !item.is_sent
    );

    if (pendingInvoices.length > 0) {
      const confirmGenerate = window.confirm(
        `⚠️ Sie haben noch ${pendingInvoices.length} nicht bestätigte Dokumente!\\n\\n` +
          `Möchten Sie trotzdem fortfahren?\\n\\n` +
          `Bitte bestätigen Sie zuerst den Status der vorherigen Dokumente in der Historie.`
      );

      if (!confirmGenerate) {
        // Wechsle zur Historie
        setActiveTab('historie');
        return;
      }
    }

    // Validation
    if (currentData.positionen.every(pos => !pos.beschreibung.trim())) {
      alert('Bitte geben Sie mindestens eine Position ein.');
      return;
    }

    setGenerating(true);

    try {
      // NEU: Speichere Daten in Storage vor PDF-Generierung
      const storageSaved = await saveGeneratorDataToStorage(currentData, activeTab as 'rechnung' | 'offerte' | 'auszahlung');
      if (storageSaved) {
        console.log('✅ Generator data saved to storage before PDF generation');
      }

      // Prüfe ob Rechnungsnummer bereits verwendet wurde
      const existingInvoice = invoiceHistory.find(
        item => item.invoice_number === currentData.rechnungsNummer && item.invoice_type === activeTab
      );

      let invoiceId: string | null = null;

      if (existingInvoice) {
        // Rechnungsnummer wird wiederverwendet
        invoiceId = existingInvoice.id;
        await reuseInvoiceNumber(invoiceId);
      } else {
        // Neue Rechnung speichern
        invoiceId = await saveInvoiceToHistory(currentData, bruttoBetrag);
      }

      const selectedKontakt = kontakte.find(k => k.name === currentData.kontakt);
      const isOfferte = activeTab === 'offerte';
      const isAuszahlung = activeTab === 'auszahlung';
      const documentTitle = isAuszahlung ? 'Auszahlung' : isOfferte ? 'Offerte' : 'Rechnung';

      // KOMPLETT ÜBERARBEITETE HTML - NUR EINE SEITE, KEINE ZWEITE SEITE
      const htmlContent = `
        <!DOCTYPE html>
        <html lang="de">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>${documentTitle} ${currentData.rechnungsNummer}</title>
          <style>
            /* === ABSOLUT STRENGE A4 EINSCHRÄNKUNG === */
            @page {
              size: A4;
              margin: 8mm;
            }
            
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
              page-break-after: avoid !important;
              page-break-inside: avoid !important;
            }
            
            html, body {
              width: 210mm !important;
              max-width: 210mm !important;
              height: 297mm !important;
              max-height: 297mm !important;
              overflow: hidden !important;
              font-family: 'Arial', sans-serif;
              font-size: 8pt;
              line-height: 1.2;
              color: #000;
              background: white !important;
              page-break-after: avoid !important;
              page-break-inside: avoid !important;
            }
            
            .document {
              width: 194mm !important;
              max-width: 194mm !important;
              height: 281mm !important;
              max-height: 281mm !important;
              margin: 0 auto;
              padding: 0;
              overflow: hidden !important;
              display: flex;
              flex-direction: column;
              background: white !important;
              page-break-after: avoid !important;
              page-break-inside: avoid !important;
              position: relative;
            }

            /* LOGO HINTER FIRMENNAME */
            .company-info {
              flex: 1;
              max-width: 85mm;
              z-index: 2;
              position: relative;
            }
            
            .company-info::after {
              content: '';
              position: absolute;
              top: 0;
              right: -25mm;
              width: 20mm;
              height: 20mm;
              background-image: url('https://static.readdy.ai/image/19164d6e6195eaf5ae2814b8eb9e837d/5f61829d7afb86e6491644b8aa8c5373.jfif');
              background-size: contain;
              background-repeat: no-repeat;
              background-position: center;
              opacity: 0.3;
              z-index: 1;
              pointer-events: none;
            }
            
            .header {
              background: white !important;
              color: #000 !important;
              padding: 6mm;
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              border-bottom: 1px solid #e5e7eb;
              flex-shrink: 0;
              position: relative;
              overflow: hidden;
              z-index: 2;
            }
            
            .company-name {
              font-size: 16pt;
              font-weight: bold;
              margin-bottom: 2mm;
              line-height: 1.2;
              color: #000;
            }
            
            .company-details {
              font-size: 8pt;
              line-height: 1.4;
              color: #000;
            }
            
            .document-info {
              text-align: right;
              flex: 1;
              max-width: 85mm;
              z-index: 2;
              position: relative;
            }
            
            .document-title {
              font-size: 18pt;
              font-weight: bold;
              margin-bottom: 2mm;
              line-height: 1.1;
              color: #000;
            }
            
            .document-number {
              font-size: 10pt;
              font-weight: 500;
              margin-bottom: 3mm;
              color: #374151;
              display: inline-block;
            }
            
            .document-dates {
              font-size: 8pt;
              line-height: 1.3;
              color: #000;
            }
            
            .customer-section {
              margin: 5mm;
              padding: 4mm;
              background: white !important;
              border-left: 1px solid #e5e5e5;
              flex-shrink: 0;
              position: relative;
            }
            
            .customer-title {
              font-size: 9pt;
              font-weight: bold;
              color: #000;
              margin-bottom: 2mm;
              display: flex;
              align-items: center;
            }
            
            .customer-address {
              font-size: 8pt;
              line-height: 1.4;
              color: #000;
            }
            
            .subject-section {
              margin: 0 5mm 4mm 5mm;
              padding: 3mm;
              background: white !important;
              border-left: 1px solid #e5e5e5;
              flex-shrink: 0;
            }
            
            .subject-title {
              font-size: 9pt;
              font-weight: bold;
              color: #000;
              margin-bottom: 1mm;
              display: flex;
              align-items: center;
            }
            
            .subject-content {
              font-size: 8pt;
              line-height: 1.4;
              color: #000;
            }
            
            .positions-table {
              width: calc(100% - 10mm);
              margin: 0 5mm 4mm 5mm;
              border-collapse: separate;
              border-spacing: 0;
              font-size: 7pt;
              flex-shrink: 0;
              border-radius: 8px;
              overflow: hidden;
              box-shadow: 0 2px 8px rgba(0,0,0,0.1);
              border: 1px solid white !important;
              background: white !important;
              page-break-inside: avoid !important;
            }
            
            .positions-table th {
              background: ${isAuszahlung ? '#fef3c7' : isOfferte ? '#dcfce7' : '#fed7aa'} !important;
              color: #000 !important;
              padding: 3mm 2mm;
              text-align: left;
              font-weight: bold;
              font-size: 8pt;
              line-height: 1.2;
              border-bottom: none !important;
              border: none !important;
            }
            
            .positions-table th:first-child { 
              width: 8%; 
              text-align: center; 
            }
            .positions-table th:nth-child(2) { 
              width: 54%; 
            }
            .positions-table th:nth-child(3) { 
              width: 13%; 
              text-align: right; 
            }
            .positions-table th:nth-child(4) { 
              width: 13%; 
              text-align: right; 
            }
            .positions-table th:nth-child(5) { 
              width: 12%; 
              text-align: right; 
            }
            
            .positions-table tbody tr:nth-child(odd) {
              background: white !important;
            }
            
            .positions-table tbody tr:nth-child(even) {
              background: white !important;
            }
            
            .positions-table td {
              padding: 2.5mm 2mm;
              border-bottom: 1px solid #f3f4f6;
              vertical-align: top;
              font-size: 7pt;
              line-height: 1.3;
              color: #000 !important;
              background: inherit !important;
              border: none !important;
            }
            
            .positions-table td:first-child { 
              text-align: center; 
              font-weight: bold;
              color: #000 !important;
            }
            .positions-table td:nth-child(3),
            .positions-table td:nth-child(4),
            .positions-table td:nth-child(5) { 
              text-align: right;
              font-family: 'Arial', sans-serif;
              font-weight: 500;
              color: #000 !important;
            }

            /* NEU: Vertragsbedingungen Tabelle */
            .contract-conditions-table {
              width: calc(100% - 10mm);
              margin: 0 5mm 4mm 5mm;
              border-collapse: separate;
              border-spacing: 0;
              font-size: 7pt;
              flex-shrink: 0;
              border-radius: 8px;
              overflow: hidden;
              box-shadow: 0 2px 8px rgba(0,0,0,0.1);
              border: 1px solid white !important;
              background: white !important;
              page-break-inside: avoid !important;
            }
            
            .contract-conditions-table th {
              background: #dbeafe !important;
              color: #000 !important;
              padding: 3mm 2mm;
              text-align: left;
              font-weight: bold;
              font-size: 8pt;
              line-height: 1.2;
              border-bottom: none !important;
              border: none !important;
            }
            
            .contract-conditions-table th:first-child { 
              width: 40%; 
            }
            .contract-conditions-table th:nth-child(2) { 
              width: 60%; 
            }
            
            .contract-conditions-table td {
              padding: 2.5mm 2mm;
              border-bottom: 1px solid #f3f4f6;
              vertical-align: top;
              font-size: 7pt;
              line-height: 1.3;
              color: #000 !important;
              background: white !important;
              border: none !important;
            }
            
            .contract-conditions-table td:first-child { 
              font-weight: bold;
              color: #374151 !important;
            }

            .totals-section {
              display: flex;
              justify-content: flex-end;
              margin: 0 5mm 4mm 5mm;
              flex-shrink: 0;
              page-break-inside: avoid !important;
            }
            
            .totals-table {
              border-collapse: separate;
              border-spacing: 0;
              width: 100% !important;
              border-radius: 0px !important;
              overflow: hidden;
              box-shadow: none !important;
              border: 1px solid white !important;
              background: white !important;
            }
            
            .totals-table td {
              padding: 3mm 4mm;
              font-size: 8pt;
              color: #000 !important;
              border-bottom: 1px solid #f3f4f6;
              border: none !important;
              box-shadow: none !important;
            }
            
            .totals-table tr:nth-child(odd) td:first-child {
              background: white !important;
              font-weight: 600;
              color: #000 !important;
            }
            
            .totals-table tr:nth-child(even) td:first-child {
              background: white !important;
              font-weight: 600;
              color: #000 !important;
            }
            
            .totals-table td:last-child {
              text-align: right;
              font-family: 'Arial', sans-serif;
              font-weight: bold;
              background: white !important;
              color: #000 !important;
            }

            .final-total {
              background: white !important;
              color: #000 !important;
              font-size: 10pt !important;
              font-weight: bold !important;
            }
            
            .final-total td {
              background: white !important;
              color: #000 !important;
            }

            .info-section {
              margin: 0 5mm 3mm 5mm;
              padding: 3mm;
              background: white !important;
              border-radius: 0px !important;
              border: 1px solid white !important;
              flex-shrink: 0;
            }
            
            .info-title {
              font-size: 9pt;
              font-weight: bold;
              color: #000;
              margin-bottom: 2mm;
            }
            
            .info-content {
              font-size: 8pt;
              line-height: 1.4;
              color: #000;
            }
            
            .footer {
              margin-top: auto;
              padding: 3mm 5mm;
              background: white !important;
              border-top: 1px solid #e5e7eb;
              text-align: center;
              font-size: 7pt;
              color: #000;
              flex-shrink: 0;
            }

            /* PRINT MEDIA - ABSOLUT STRENGE EINSCHRÄNKUNGEN */
            @media print {
              html, body {
                width: 210mm !important;
                height: 297mm !important;
                max-width: 210mm !important;
                max-height: 297mm !important;
                overflow: hidden !important;
                background: white !important;
                font-family: 'Arial', sans-serif !important;
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
                print-color-adjust: exact !important;
                page-break-after: avoid !important;
                page-break-inside: avoid !important;
              }
              
              .document {
                width: 194mm !important;
                height: 281mm !important;
                max-width: 194mm !important;
                max-height: 281mm !important;
                page-break-inside: avoid !important;
                page-break-after: avoid !important;
                overflow: hidden !important;
                box-shadow: none !important;
                background: white !important;
              }
              
              .positions-table {
                page-break-inside: avoid !important;
                background: white !important;
              }
              
              .positions-table th {
                background: ${isAuszahlung ? '#fef3c7' : isOfferte ? '#dcfce7' : '#fed7aa'} !important;
                color: #000 !important;
              }

              .contract-conditions-table {
                page-break-inside: avoid !important;
                background: white !important;
              }
              
              .contract-conditions-table th {
                background: #dbeafe !important;
                color: #000 !important;
              }

              .totals-section {
                page-break-inside: avoid !important;
              }
              
              .no-print { 
                display: none !important; 
              }
              
              * {
                page-break-after: avoid !important;
                page-break-inside: avoid !important;
              }
              
              /* VERHINDERE ZWEITE SEITE KOMPLETT */
              body::after {
                content: "";
                display: block;
                page-break-after: avoid !important;
              }

              /* ENTFERNE BROWSER-HEADER UND -FOOTER KOMPLETT */
              @page {
                size: A4;
                margin: 8mm;
                /* ENTFERNE ALLE BROWSER-ELEMENTE */
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
                print-color-adjust: exact !important;
              }

              /* VERSTECKE ALLE BROWSER-ELEMENTE */
              html {
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
            }
            
            .download-btn {
              position: fixed;
              top: 20px;
              right: 20px;
              background: linear-gradient(135deg, ${isAuszahlung ? '#f59e0b' : isOfferte ? '#10b981' : '#3b82f6'} 0%, ${isAuszahlung ? '#d97706' : isOfferte ? '#059669' : '#1d4ed8'} 100%);
              color: white;
              border: none;
              padding: 12px 24px;
              border-radius: 25px;
              font-size: 12pt;
              font-weight: bold;
              cursor: pointer;
              z-index: 1000;
              box-shadow: 0 4px 15px rgba(0,0,0,0.2);
              transition: all 0.3s ease;
            }
            
            .download-btn:hover {
              background: linear-gradient(135deg, ${isAuszahlung ? '#d97706' : isOfferte ? '#059669' : '#1d4ed8'} 0%, ${isAuszahlung ? '#b45309' : isOfferte ? '#047857' : '#1e40af'} 100%);
              transform: translateY(-2px);
              box-shadow: 0 6px 20px rgba(0,0,0,0.3);
            }
            
            .truncate {
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              font-family: 'Arial', sans-serif;
            }
            
            .word-break {
              word-break: break-word;
              hyphens: auto;
              font-family: 'Arial', sans-serif;
            }
          </style>
          <script>
            function downloadPDF() {
              window.print();
            }
            
            // VERHINDERE ZWEITE SEITE - NUR EINMAL DRUCKEN
            window.onload = function() {
              setTimeout(function() {
                window.print();
                // SCHLIESSE FENSTER NACH DRUCK
                setTimeout(function() {
                  window.close();
                }, 2000);
              }, 1000);
            };
            
            // VERHINDERE MEHRFACHE AUSFÜHRUNG
            window.onbeforeprint = function() {
              console.log('Print started - single page only');
            };
            
            window.onafterprint = function() {
              console.log('Print finished - closing');
              window.close();
            };
          </script>
        </head>
        <body>
          <button class="download-btn no-print" onclick="downloadPDF()">
            PDF Speichern
          </button>

          <div class="document">
            <!-- HEADER -->
            <div class="header">
              <div class="company-info">
                <div class="company-name">${currentData.firmenName}</div>
                <div class="company-details">
                  ${currentData.firmenAdresse.replace(/\\n/g, '<br>')}<br>
                  ${currentData.firmenTelefon}<br>
                  ${currentData.firmenEmail}
                </div>
              </div>
              <div class="document-info">
                <div class="document-title">${documentTitle}</div>
                <div class="document-number">Nr: ${currentData.rechnungsNummer}</div>
                <div class="document-dates">
                  <strong>Datum:</strong> ${new Date(currentData.rechnungsDatum).toLocaleDateString('de-CH')}<br>
                  <strong>${isAuszahlung ? 'Auszahlung bis:' : isOfferte ? 'Gültig bis:' : 'Fällig:'}</strong> ${new Date(currentData.faelligkeitsDatum).toLocaleDateString('de-CH')}
                </div>
              </div>
            </div>

            <!-- KUNDE -->
            <div class="customer-section">
              <div class="customer-title">${isAuszahlung ? 'Auszahlung an:' : isOfferte ? 'Angebot für:' : 'Rechnungsempfänger:'}</div>
              <div class="customer-address">
                ${currentData.kontakt ? `
                  <strong>${selectedKontakt?.name || currentData.kontakt}</strong><br>
                  ${selectedKontakt?.firma ? selectedKontakt.firma + '<br>' : ''}
                  ${selectedKontakt?.adresse ? selectedKontakt.adresse + '<br>' : ''}
                  ${selectedKontakt?.plz || ''} ${selectedKontakt?.ort || ''}<br>
                  ${selectedKontakt?.email ? 'Email: ' + selectedKontakt.email + '<br>' : ''}
                  ${selectedKontakt?.telefon ? 'Tel: ' + selectedKontakt.telefon : ''}
                ` : `
                  <strong>Kunde</strong><br>
                  Adresse wird nachgetragen
                `}
              </div>
            </div>

            <!-- BETRAG -->
            ${currentData.betreff ? `
              <div class="subject-section">
                <div class="subject-title">Betreff:</div>
                <div class="subject-content word-break" style="font-size: 14pt; font-weight: 700; line-height: 1.4; color: #1f2937; margin-top: 2mm;">${currentData.betreff}</div>
              </div>
            ` : ''}

            <!-- BESCHREIBUNG -->
            ${currentData.beschreibung ? `
              <div class="subject-section">
                <div class="subject-content word-break">${currentData.beschreibung}</div>
              </div>
            ` : ''}

            <!-- POSITIONEN TABELLE - MAXIMAL 8 POSITIONEN -->
            <table class="positions-table">
              <thead>
                <tr>
                  <th>Pos.</th>
                  <th>Beschreibung</th>
                  <th>Menge</th>
                  <th>Einzelpreis</th>
                  <th>Gesamt</th>
                </tr>
              </thead>
              <tbody>
                ${currentData.positionen
                  .filter(pos => pos.beschreibung.trim())
                  .slice(0, 8)
                  .map((pos, index) => `
                    <tr>
                      <td>${index + 1}</td>
                      <td class="word-break">${pos.beschreibung.length > 60 ? pos.beschreibung.substring(0, 60) + '...' : pos.beschreibung}</td>
                      <td>${pos.menge}</td>
                      <td>CHF ${pos.einzelpreis.toFixed(2)}</td>
                      <td><strong>CHF ${pos.gesamt.toFixed(2)}</strong></td>
                    </tr>
                  `).join('')}
              </tbody>
            </table>

            <!-- VERTRAGSBEDINGUNGEN TABELLE - NUR BEI OFFERTEN -->
            ${isOfferte && currentData.vertragsbedingungen && currentData.vertragsbedingungen.filter(bed => bed.bedingung.trim() && bed.wert.trim()).length > 0 ? `
              <table class="contract-conditions-table">
                <thead>
                  <tr>
                    <th>Vertragsbedingung</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  ${currentData.vertragsbedingungen
                    .filter(bed => bed.bedingung.trim() && bed.wert.trim())
                    .slice(0, 6)
                    .map(bedingung => `
                      <tr>
                        <td class="word-break">${bedingung.bedingung}</td>
                        <td class="word-break">${bedingung.wert}</td>
                      </tr>
                    `).join('')}
                </tbody>
              </table>
            ` : ''}

            <!-- SUMMEN MIT RABATTEN -->
            <div class="totals-section">
              <table class="totals-table">
                <tr>
                  <td>Zwischensumme:</td>
                  <td>CHF ${(zwischensumme || 0).toFixed(2)}</td>
                </tr>
                ${gesamtRabatt > 0 ? `
                <tr class="discount-row">
                  <td>${activeTab === 'auszahlung' ? 'Abzüge:' : 'Gesamtrabatt:'}</td>
                  <td>-CHF ${(gesamtRabatt || 0).toFixed(2)}</td>
                </tr>
                ` : ''}
                <tr>
                  <td>Nettobetrag:</td>
                  <td>CHF ${(nettoBetrag || 0).toFixed(2)}</td>
                </tr>
                ${currentData.mehrwertsteuer > 0 ? `
                <tr>
                  <td>MwSt. (${currentData.mehrwertsteuer}%):</td>
                  <td>CHF ${(mwstBetrag || 0).toFixed(2)}</td>
                </tr>
                ` : ''}
                <tr class="final-total">
                  <td>${activeTab === 'rechnung' ? 'Gesamtbetrag:' : activeTab === 'offerte' ? 'Angebotssumme:' : 'Auszahlungsbetrag:'}</td>
                  <td>CHF ${(bruttoBetrag || 0).toFixed(2)}</td>
                </tr>
              </table>
            </div>

            <!-- Rabatt Übersicht -->
            ${gesamtRabatt > 0 ? `
              <div class="mt-4 pt-3 border-t border-orange-200">
                <h4 class="font-medium text-gray-700 mb-2 flex items-center">
                  <i class="ri-price-tag-3-line mr-2 text-orange-600"></i>
                  ${activeTab === 'auszahlung' ? 'Angewandte Abzüge:' : 'Angewandte Rabatte:'}
                </h4>
                <div class="space-y-1">
                  ${currentData.rabatte
                    .filter(r => r.beschreibung.trim())
                    .map((rabatt) => `
                      <div class="flex justify-between text-xs text-orange-600">
                        <span>
                          ${rabatt.beschreibung} (${rabatt.typ === 'prozent' ? rabatt.wert + '%' : 'CHF ' + (Number(rabatt.wert) || 0).toFixed(2)}):
                        </span>
                        <span>-CHF ${(Number(rabatt.betrag) || 0).toFixed(2)}</span>
                      </div>
                    `).join('')}
                </div>
              </div>
            ` : ''}

            <!-- ZAHLUNGSKONDITIONEN - AM SCHLUSS BEI OFFERTEN -->
            ${isOfferte ? `
              <div class="info-section payment-section">
                <div class="info-title">Zahlungskonditionen bei Auftragserteilung</div>
                <div class="info-content word-break">${currentData.zahlungskonditionen.length > 120 ? currentData.zahlungskonditionen.substring(0, 120) + '...' : currentData.zahlungskonditionen}</div>
              </div>
            ` : ''}

            <!-- ZAHLUNGSKONDITIONEN - BEI RECHNUNGEN UND AUSZAHLUNGEN -->
            ${!isOfferte ? `
              <div class="info-section payment-section">
                <div class="info-title">${isAuszahlung ? 'Auszahlungskonditionen:' : 'Zahlungskonditionen'}</div>
                <div class="info-content word-break">${currentData.zahlungskonditionen.length > 120 ? currentData.zahlungskonditionen.substring(0, 120) + '...' : currentData.zahlungskonditionen}</div>
              </div>
            ` : ''}

            <!-- FOOTER - NUR BEI RECHNUNGEN UND AUSZAHLUNGEN -->
            ${!isOfferte ? `
            <div class="footer">
              <p><strong>IID:</strong> ${currentData.steuernummer} | <strong>USt-IdNr.:</strong> ${currentData.ustIdNr}</p>
              <p>Erstellt am ${new Date().toLocaleDateString('de-CH')} • ${currentData.firmenName} • ${currentData.firmenEmail}</p>
            </div>
            ` : ''}

          </div>
        </body>
        </html>
      `;

      // Warnung bei zu vielen Positionen
      const validPositions = currentData.positionen.filter(pos => pos.beschreibung.trim());
      const validDiscounts = currentData.rabatte.filter(r => r.beschreibung.trim());

      if (validPositions.length > 8) {
        alert(`⚠️ Hinweis: Sie haben ${validPositions.length} Positionen eingegeben.\\n\\nFür eine garantiert einseitige PDF werden nur die ersten 8 Positionen angezeigt.\\n\\nBitte reduzieren Sie die Anzahl der Positionen oder erstellen Sie mehrere separate Dokumente.`);
      }

      if (validDiscounts.length > 5) {
        alert(`⚠️ Hinweis: Sie haben ${validDiscounts.length} Rabatte eingegeben.\\n\\nFür eine garantiert einseitige PDF werden nur die ersten 5 Rabatte angezeigt.`);
      }

      // HTML-Datei erstellen und herunterladen
      const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);

      // Download HTML file
      const downloadLink = document.createElement('a');
      downloadLink.href = url;
      downloadLink.download = `${documentTitle}_${currentData.rechnungsNummer}_${new Date().toISOString().split('T')[0]}.html`;
      downloadLink.style.display = 'none';
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      // KOMPLETT NEUES PRINT-SYSTEM - NUR EINE SEITE
      const printWindow = window.open('', '_blank', 'width=794,height=1123,scrollbars=no,resizable=no');
      if (printWindow) {
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        
        // Warte bis Inhalt geladen ist
        printWindow.onload = function() {
          setTimeout(() => {
            printWindow.focus();
            printWindow.print();
            // Schließe Fenster nach Print-Dialog
            setTimeout(() => {
              printWindow.close();
            }, 1000);
          }, 1000);
        };
      }

      // URL nach 30 Sekunden freigeben
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 30000);

      // NEU: Status-Bestätigungsmodal öffnen
      if (invoiceId) {
        setPendingInvoiceId(invoiceId);
        setConfirmationModal({
          isOpen: true,
          invoiceData: currentData,
          htmlContent: htmlContent,
          printWindow: printWindow
        });
      }

      if (!existingInvoice) {
        alert(`✅ ${documentTitle} wurde erfolgreich generiert!\\n\\n📄 **NUR EINE SEITE:**\\n• HTML-Datei wurde heruntergeladen\\n• Print-Dialog öffnet sich erneut in neuem Fenster\\n• KEINE zweite Seite mehr!\\n\\n💾 **Automatische Speicherung:**\\n• Alle Daten in Supabase Storage gesichert\\n• Historie wurde aktualisiert\\n\\n📊 **Status-Bestätigung:**\\nBitte bestätigen Sie nach dem Drucken, ob das Dokument versendet wurde.`);
      } else {
        alert(`🎉 Komplette Daten wurden geladen!`);
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('❌ Fehler beim Generieren des Dokuments');
    } finally {
      setGenerating(false);
    }
  };

  // NEU: Manual Save Button Handler
  const handleManualSave = async () => {
    const currentFormData = activeTab === 'rechnung' ? formData : activeTab === 'offerte' ? offertenData : auszahlungData;

    if (!currentFormData.positionen.some(pos => pos.beschreibung.trim())) {
      alert('⚠️ Bitte füllen Sie mindestens eine Position aus, bevor Sie speichern.');
      return;
    }

    const saved = await saveGeneratorDataToStorage(currentFormData, activeTab as 'rechnung' | 'offerte' | 'auszahlung');
    if (saved) {
      alert('✅ Generator-Daten wurden erfolgreich in Supabase Storage gespeichert!');
    } else {
      alert('❌ Fehler beim Speichern der Daten');
    }
  };

  // Kontakt-Optionen
  const kontaktOptionen = kontakte.map(kontakt => ({
    value: kontakt.name,
    label: kontakt.firma ? `${kontakt.name} (${kontakt.firma})` : kontakt.name
  }));

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Lade Generator...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Mobile-optimized Header */}
      <div className="border-b border-gray-200">
        <div className="w-full px-3 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center sm:hidden">
                <i className="ri-file-text-line text-purple-600 text-lg"></i>
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Dokument Generator</h1>
                <p className="text-sm text-gray-600 mt-1">
                  Professionelle Rechnungen, Offerten und Auszahlungen mit intelligenter Nummernverwaltung
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2 px-3 py-1.5 bg-green-100 rounded-lg">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-sm font-medium text-green-700">🇨🇭 Schweiz</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile-optimized Tab Navigation */}
      <div className="border-b border-gray-200">
        <div className="w-full px-3 sm:px-6">
          <div className="flex overflow-x-auto space-x-1 sm:space-x-8 scrollbar-hide">
            <button
              onClick={() => setActiveTab('rechnung')}
              className={`py-4 px-3 sm:px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'rechnung'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="ri-bill-line mr-2"></i>
              <span className="hidden sm:inline">Rechnung Generator</span>
              <span className="sm:hidden">Rechnung</span>
            </button>
            <button
              onClick={() => setActiveTab('offerte')}
              className={`py-4 px-3 sm:px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'offerte'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="ri-price-tag-3-line mr-2"></i>
              <span className="hidden sm:inline">Offerten Generator</span>
              <span className="sm:hidden">Offerte</span>
            </button>
            <button
              onClick={() => setActiveTab('auszahlung')}
              className={`py-4 px-3 sm:px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'auszahlung'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="ri-hand-coin-line mr-2"></i>
              <span className="hidden sm:inline">Auszahlung Generator</span>
              <span className="sm:hidden">Auszahlung</span>
            </button>
            <button
              onClick={() => setActiveTab('historie')}
              className={`py-4 px-3 sm:px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'historie'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="ri-history-line mr-2"></i>
              <span className="hidden sm:inline">Dokumenthistorie</span>
              <span className="sm:hidden">Historie</span>
              {invoiceHistory.filter(item => item.status === 'generiert' && !item.is_sent).length > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                  {invoiceHistory.filter(item => item.status === 'generiert' && !item.is_sent).length}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="w-full px-3 sm:px-6 py-4 sm:py-6">
        {activeTab === 'historie' ? (
          // Mobile-optimized Historie Tab
          <div className="max-w-6xl mx-auto">
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                      <i className="ri-history-line mr-2 text-purple-600"></i>
                      Dokumenthistorie &amp; Nummernverwaltung
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      Alle generierten Dokumente und deren Status-Verfolgung
                    </p>
                  </div>
                  <Button
                    onClick={loadInvoiceHistory}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap"
                    disabled={historyLoading}
                  >
                    {historyLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Lade...
                      </>
                    ) : (
                      <>
                        <i className="ri-refresh-line mr-2"></i>
                        <span className="hidden sm:inline">Aktualisieren</span>
                        <span className="sm:hidden">Update</span>
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {historyLoading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Lade Historie...</p>
                </div>
              ) : invoiceHistory.length === 0 ? (
                <div className="p-8 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-history-line text-2xl text-gray-400"></i>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Keine Historie vorhanden</h3>
                  <p className="text-gray-600">
                    Generieren Sie Ihr erstes Dokument, um die Historie zu sehen.
                  </p>
                </div>
              ) : (
                // Mobile-optimized Table
                <div className="overflow-x-auto">
                  {/* Mobile Card View */}
                  <div className="block sm:hidden">
                    <div className="divide-y divide-gray-200">
                      {invoiceHistory.map(item => (
                        <div
                          key={item.id}
                          className={`p-4 ${
                            item.status === 'generiert' && !item.is_sent ? 'bg-yellow-50' : ''
                          }`}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center">
                              <div
                                className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 ${
                                  item.invoice_type === 'rechnung' ? 'bg-blue-100' : 
                                  item.invoice_type === 'offerte' ? 'bg-green-100' : 'bg-orange-100'
                                }`}
                              >
                                <i
                                  className={`${
                                    item.invoice_type === 'rechnung' ? 'ri-bill-line' : 
                                    item.invoice_type === 'offerte' ? 'ri-price-tag-3-line' : 'ri-hand-coin-line'
                                  } text-${
                                    item.invoice_type === 'rechnung' ? 'blue' : 
                                    item.invoice_type === 'offerte' ? 'green' : 'orange'
                                  }-600 text-sm`}
                                ></i>
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">{item.invoice_number}</div>
                                <div className="text-xs text-gray-500 capitalize">
                                  {item.invoice_type}
                                  {item.pdf_generated_count > 1 && (
                                    <span className="ml-1 text-orange-600">
                                      ({item.pdf_generated_count}x)
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                item.status === 'generiert' && !item.is_sent
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : item.status === 'versendet'
                                  ? 'bg-green-100 text-green-800'
                                  : item.status === 'bezahlt'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {item.status === 'generiert' && !item.is_sent
                                ? '⏳ Ausstehend'
                                : item.status === 'versendet'
                                ? '✅ Versendet'
                                : item.status === 'bezahlt'
                                ? '💰 Bezahlt'
                                : '❌ Storniert'}
                            </span>
                          </div>
                          
                          <div className="space-y-2 mb-3">
                            <div className="text-sm text-gray-900 truncate">{item.contact_name}</div>
                            <div className="text-xs text-gray-500 truncate">{item.subject}</div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-medium text-gray-900">
                                CHF {item.total_amount.toFixed(2)}
                              </span>
                              <span className="text-xs text-gray-500">
                                {new Date(item.invoice_date).toLocaleDateString('de-DE')}
                              </span>
                            </div>
                          </div>

                          <div className="flex flex-wrap gap-2">
                            {item.status === 'generiert' && !item.is_sent && (
                              <>
                                <button
                                  onClick={() => {
                                    // NEU: Lade komplette Daten statt nur Nummer
                                    loadInvoiceDataFromHistory(item);
                                    setActiveTab(item.invoice_type as 'rechnung' | 'offerte' | 'auszahlung');
                                  }}
                                  className="text-blue-600 hover:text-blue-700 cursor-pointer px-2 py-1 rounded hover-bg-blue-50 text-xs"
                                >
                                  🔄 Wiederverwenden
                                </button>
                                <button
                                  onClick={() => {
                                    // NEU: Lade komplette Daten statt nur Nummer  
                                    loadInvoiceDataFromHistory(item);
                                    setActiveTab(item.invoice_type as 'rechnung' | 'offerte' | 'auszahlung');
                                  }}
                                  className="text-blue-600 hover:text-blue-700 cursor-pointer px-2 py-1 rounded hover-bg-blue-50"
                                  title="Nummer wiederverwenden"
                                >
                                  🔄 Wiederverwenden
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => deleteInvoiceFromHistory(item.id, item.invoice_number)}
                              className="text-red-600 hover:text-red-700 cursor-pointer px-2 py-1 rounded hover-bg-red-50 text-xs"
                            >
                              🗑️ Löschen
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Desktop Table View */}
                  <table className="w-full hidden sm:table">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Nummer & Typ
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Kunde & Betreff
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Betrag & Datum
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Aktionen
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {invoiceHistory.map(item => (
                        <tr
                          key={item.id}
                          className={`hover:bg-gray-50 ${
                            item.status === 'generiert' && !item.is_sent ? 'bg-yellow-50' : ''
                          }`}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div
                                className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 ${
                                  item.invoice_type === 'rechnung' ? 'bg-blue-100' : 
                                  item.invoice_type === 'offerte' ? 'bg-green-100' : 'bg-orange-100'
                                }`}
                              >
                                <i
                                  className={`${
                                    item.invoice_type === 'rechnung' ? 'ri-bill-line' : 
                                    item.invoice_type === 'offerte' ? 'ri-price-tag-3-line' : 'ri-hand-coin-line'
                                  } text-${
                                    item.invoice_type === 'rechnung' ? 'blue' : 
                                    item.invoice_type === 'offerte' ? 'green' : 'orange'
                                  }-600 text-sm`}
                                ></i>
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">{item.invoice_number}</div>
                                <div className="text-xs text-gray-500 capitalize">
                                  {item.invoice_type}
                                  {item.pdf_generated_count > 1 && (
                                    <span className="ml-1 text-orange-600">
                                      ({item.pdf_generated_count}x)
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900 truncate max-w-xs">
                              {item.contact_name}
                            </div>
                            <div className="text-xs text-gray-500 truncate max-w-xs">{item.subject}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              CHF {item.total_amount.toFixed(2)}
                            </div>
                            <div className="text-xs text-gray-500">
                              {new Date(item.invoice_date).toLocaleDateString('de-DE')}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                item.status === 'generiert' && !item.is_sent
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : item.status === 'versendet'
                                  ? 'bg-green-100 text-green-800'
                                  : item.status === 'bezahlt'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {item.status === 'generiert' && !item.is_sent
                                ? '⏳ Ausstehend'
                                : item.status === 'versendet'
                                ? '✅ Versendet'
                                : item.status === 'bezahlt'
                                ? '💰 Bezahlt'
                                : '❌ Storniert'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                            {item.status === 'generiert' && !item.is_sent && (
                              <>
                                <button
                                  onClick={() => {
                                    // NEU: Lade komplette Daten statt nur Nummer
                                    loadInvoiceDataFromHistory(item);
                                    setActiveTab(item.invoice_type as 'rechnung' | 'offerte' | 'auszahlung');
                                  }}
                                  className="text-blue-600 hover:text-blue-700 cursor-pointer px-2 py-1 rounded hover-bg-blue-50 text-xs"
                                >
                                  🔄 Wiederverwenden
                                </button>
                                <button
                                  onClick={() => {
                                    // NEU: Lade komplette Daten statt nur Nummer  
                                    loadInvoiceDataFromHistory(item);
                                    setActiveTab(item.invoice_type as 'rechnung' | 'offerte' | 'auszahlung');
                                  }}
                                  className="text-blue-600 hover:text-blue-700 cursor-pointer px-2 py-1 rounded hover-bg-blue-50"
                                  title="Nummer wiederverwenden"
                                >
                                  🔄 Wiederverwenden
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => deleteInvoiceFromHistory(item.id, item.invoice_number)}
                              className="text-red-600 hover:text-red-700 cursor-pointer px-2 py-1 rounded hover-bg-red-50 text-xs"
                            >
                              🗑️ Löschen
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Mobile-optimized Statistiken */}
              {invoiceHistory.length > 0 && (
                <div className="px-4 sm:px-6 py-4 bg-gray-50 border-t border-gray-200">
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                    <div className="text-center">
                      <div className="text-xl sm:text-2xl font-bold text-gray-900">{invoiceHistory.length}</div>
                      <div className="text-xs text-gray-500">Gesamt</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl sm:text-2xl font-bold text-yellow-600">
                        {invoiceHistory.filter(item => item.status === 'generiert' && !item.is_sent).length}
                      </div>
                      <div className="text-xs text-gray-500">Ausstehend</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl sm:text-2xl font-bold text-green-600">
                        {invoiceHistory.filter(item => item.status === 'versendet').length}
                      </div>
                      <div className="text-xs text-gray-500">Versendet</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl sm:text-2xl font-bold text-orange-600">
                        {invoiceHistory.filter(item => item.invoice_type === 'auszahlung').length}
                      </div>
                      <div className="text-xs text-gray-500">Auszahlungen</div>
                    </div>
                    <div className="text-center col-span-2 sm:col-span-1">
                      <div className="text-xl sm:text-2xl font-bold text-blue-600">
                        CHF {invoiceHistory.reduce((sum, item) => sum + item.total_amount, 0).toFixed(2)}
                      </div>
                      <div className="text-xs text-gray-500">Gesamtsumme</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          // Mobile-optimized Form Content
          <div className="max-w-4xl mx-auto">
            <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
              <form className="space-y-6">
                {/* Mobile-optimized Firmeninformationen */}
                <div
                  className={`${
                    activeTab === 'rechnung' ? 'bg-blue-50 border-blue-200' : 
                    activeTab === 'offerte' ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'
                  } p-4 rounded-lg border`}
                >
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <i className="ri-building-line mr-2"></i>
                    Ihre Firmeninformationen
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      label="Firmenname"
                      value={currentData.firmenName}
                      onChange={e => setCurrentData(prev => ({ ...prev, firmenName: e.target.value }))}
                      placeholder="Dä Snäckomat Plüss"
                    />
                    <Input
                      label="Telefon"
                      value={currentData.firmenTelefon}
                      onChange={e => setCurrentData(prev => ({ ...prev, firmenTelefon: e.target.value }))}
                      placeholder="+41 76 629 09 01"
                    />
                    <Input
                      label="Email"
                      type="email"
                      value={currentData.firmenEmail}
                      onChange={e => setCurrentData(prev => ({ ...prev, firmenEmail: e.target.value }))}
                      placeholder="info@snaeckomat.ch"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Firmenadresse</label>
                      <textarea
                        value={currentData.firmenAdresse}
                        onChange={e => setCurrentData(prev => ({ ...prev, firmenAdresse: e.target.value }))}
                        placeholder="Eichliweg 33&#10;5103 Möriken AG&#10;Schweiz"
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Bankverbindung</label>
                      <textarea
                        value={currentData.bankverbindung}
                        onChange={e => setCurrentData(prev => ({ ...prev, bankverbindung: e.target.value }))}
                        placeholder="IBAN: CH52 8080 8006 4305 2492 8&#10;IID (BC-Nr.): 80808&#10;SWIFT-BIC: RAIFCH22"
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Mobile-optimized Grunddaten */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label={activeTab === 'rechnung' ? 'Rechnungsnummer' : activeTab === 'offerte' ? 'Offerten-Nummer' : 'Auszahlungs-Nummer'}
                    value={currentData.rechnungsNummer}
                    onChange={e => setCurrentData(prev => ({ ...prev, rechnungsNummer: e.target.value }))}
                    placeholder={activeTab === 'rechnung' ? 'R-2025-001' : activeTab === 'offerte' ? 'OFF-2025-001' : 'A-2025-001'}
                  />

                  <Select
                    label="Kunde (optional)"
                    options={[
                      { value: '', label: 'Kein Kunde ausgewählt' },
                      ...kontaktOptionen
                    ]}
                    value={currentData.kontakt}
                    onChange={value => setCurrentData(prev => ({ ...prev, kontakt: value as string }))}
                    placeholder="Kunde auswählen..."
                  />

                  <Select
                    label="Mehrwertsteuer"
                    options={mehrwertsteuersaetze}
                    value={currentData.mehrwertsteuer}
                    onChange={value => setCurrentData(prev => ({ ...prev, mehrwertsteuer: Number(value) }))}
                  />

                  <Input
                    label={activeTab === 'rechnung' ? 'Rechnungsdatum' : activeTab === 'offerte' ? 'Angebotsdatum' : 'Auszahlungsdatum'}
                    type="date"
                    value={currentData.rechnungsDatum}
                    onChange={e => setCurrentData(prev => ({ ...prev, rechnungsDatum: e.target.value }))}
                  />

                  <Input
                    label={activeTab === 'rechnung' ? 'Fälligkeitsdatum' : activeTab === 'offerte' ? 'Gültig bis' : 'Auszahlung bis'}
                    type="date"
                    value={currentData.faelligkeitsDatum}
                    onChange={e => setCurrentData(prev => ({ ...prev, faelligkeitsDatum: e.target.value }))}
                  />
                </div>

                {/* Mobile-optimized Betreff & Beschreibung */}
                <div className="space-y-4">
                  <Input
                    label="Betreff (optional)"
                    value={currentData.betreff}
                    onChange={e => setCurrentData(prev => ({ ...prev, betreff: e.target.value }))}
                    placeholder={activeTab === 'rechnung' ? 'z.B. Wartung Automat XY' : activeTab === 'offerte' ? 'z.B. Angebot für Automat-Installation' : 'z.B. Umsatzbeteiligung Oktober 2024'}
                  />

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Beschreibung (optional)
                    </label>
                    <textarea
                      value={currentData.beschreibung}
                      onChange={e => setCurrentData(prev => ({ ...prev, beschreibung: e.target.value }))}
                      placeholder={activeTab === 'rechnung' ? 'Zusätzliche Informationen zur Rechnung...' : activeTab === 'offerte' ? 'Zusätzliche Informationen zum Angebot...' : 'Zusätzliche Informationen zur Auszahlung...'}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
                    />
                  </div>
                </div>

                {/* Mobile-optimized Positionen */}
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
                    <h3 className="text-lg font-medium text-gray-900">
                      {activeTab === 'rechnung' ? 'Rechnungspositionen' : activeTab === 'offerte' ? 'Angebotspositionen' : 'Auszahlungspositionen'}
                    </h3>
                    <Button
                      type="button"
                      onClick={addPosition}
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap w-full sm:w-auto"
                    >
                      <i className="ri-add-line mr-2"></i>
                      Position hinzufügen
                    </Button>
                  </div>

                  <div className="space-y-4">
                    {currentData.positionen.map((position, index) => (
                      <div key={position.id} className="bg-gray-50 p-4 rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-gray-900 flex items-center">
                            <i className="ri-price-tag-3-line mr-2 text-orange-600"></i>
                            Position {index + 1}
                          </h4>
                          {currentData.positionen.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removePosition(position.id)}
                              className="text-red-600 hover:text-red-700 p-1"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          )}
                        </div>

                        <div className="space-y-3">
                          <div>
                            <Input
                              label="Beschreibung"
                              value={position.beschreibung}
                              onChange={e => updatePosition(position.id, 'beschreibung', e.target.value)}
                              placeholder={activeTab === 'auszahlung' ? 'z.B. Umsatzbeteiligung 13%, Provision, Bonus...' : 'z.B. Designautomat, Wartung, Service...'}
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <Input
                              label="Menge"
                              type="number"
                              value={position.menge}
                              onChange={e => updatePosition(position.id, 'menge', Number(e.target.value))}
                              step="0.01"
                              min="0"
                            />
                            <Input
                              label="Einzelpreis (CHF)"
                              type="number"
                              value={position.einzelpreis}
                              onChange={e => updatePosition(position.id, 'einzelpreis', Number(e.target.value))}
                              step="0.01"
                              min="0"
                            />
                          </div>
                        </div>

                        <div className="mt-3 text-right">
                          <span className="text-sm text-gray-600">Gesamt: </span>
                          <span className="font-medium text-gray-900">CHF {(Number(position.gesamt) || 0).toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Rabatt Sektion */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center">
                      <i className="ri-price-tag-3-line mr-2 text-orange-600"></i>
                      Rabatte (optional)
                    </h3>
                    <Button
                      type="button"
                      onClick={addRabatt}
                      className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap"
                    >
                      <i className="ri-subtract-line mr-2"></i>
                      Rabatt hinzufügen
                    </Button>
                  </div>

                  {currentData.rabatte.length > 0 && (
                    <div className="space-y-4">
                      {currentData.rabatte.map((rabatt, index) => (
                        <div key={rabatt.id} className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-medium text-gray-900 flex items-center">
                              <i className="ri-price-tag-3-line mr-2 text-orange-600"></i>
                              Rabatt {index + 1}
                            </h4>
                            <button
                              type="button"
                              onClick={() => removeRabatt(rabatt.id)}
                              className="text-red-600 hover:text-red-700 p-1"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                            <div className="md:col-span-2">
                              <Input
                                label="Rabatt-Beschreibung"
                                value={rabatt.beschreibung}
                                onChange={e => updateRabatt(rabatt.id, 'beschreibung', e.target.value)}
                                placeholder="z.B. Mengenrabatt, Treuerabatt, Sonderaktion..."
                              />
                            </div>
                            <Select
                              label="Rabatt-Typ"
                              options={[
                                { value: 'prozent', label: 'Prozent (%)' },
                                { value: 'betrag', label: 'Fester Betrag (CHF)' }
                              ]}
                              value={rabatt.typ}
                              onChange={value => updateRabatt(rabatt.id, 'typ', value as string)}
                            />
                            <Input
                              label={rabatt.typ === 'prozent' ? 'Prozent (%)' : 'Betrag (CHF)'}
                              type="number"
                              value={rabatt.wert}
                              onChange={e => updateRabatt(rabatt.id, 'wert', Number(e.target.value))}
                              step={rabatt.typ === 'prozent' ? '0.1' : '0.01'}
                              min="0"
                              max={rabatt.typ === 'prozent' ? '100' : undefined}
                              placeholder={rabatt.typ === 'prozent' ? '10' : '50.00'}
                            />
                          </div>

                          <div className="mt-3 text-right">
                            <span className="text-sm text-gray-600">Rabattbetrag: </span>
                            <span className="font-medium text-orange-600">-CHF {rabatt.betrag.toFixed(2)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {currentData.rabatte.length === 0 && (
                    <div className="text-center py-8 bg-orange-50 rounded-lg border border-orange-200">
                      <div className="text-orange-400 mb-3">
                        <i className="ri-price-tag-3-line text-4xl"></i>
                      </div>
                      <h4 className="text-lg font-medium text-gray-700 mb-2">Keine Rabatte hinzugefügt</h4>
                      <p className="text-gray-500 text-sm">
                        {activeTab === 'auszahlung' 
                          ? 'Fügen Sie Abzüge oder Gebühren hinzu, falls erforderlich.'
                          : 'Fügen Sie Rabatte hinzu, um Ihren Kunden Vergünstigungen zu gewähren.'
                        }
                      </p>
                    </div>
                  )}
                </div>

                {/* Zahlungskonditionen */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {activeTab === 'rechnung' ? 'Zahlungskonditionen' : activeTab === 'offerte' ? 'Zahlungskonditionen bei Auftragserteilung' : 'Auszahlungskonditionen'}
                  </label>
                  <textarea
                    value={currentData.zahlungskonditionen}
                    onChange={e => setCurrentData(prev => ({ ...prev, zahlungskonditionen: e.target.value }))}
                    placeholder={activeTab === 'auszahlung' ? 'Auszahlungskonditionen eingeben...' : 'Zahlungskonditionen eingeben...'}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
                  />
                </div>

                {/* Vertragsbedingungen nur bei Offerten */}
                {activeTab === 'offerte' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium text-gray-900 flex items-center">
                        <i className="ri-contract-line mr-2 text-blue-600"></i>
                        Vertragsbedingungen (optional)
                      </h3>
                      <Button
                        type="button"
                        onClick={addVertragsbedingung}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap"
                      >
                        <i className="ri-add-line mr-2"></i>
                        Bedingung hinzufügen
                      </Button>
                    </div>

                    {currentData.vertragsbedingungen.length > 0 && (
                      <div className="space-y-4">
                        {currentData.vertragsbedingungen.map((bedingung, index) => (
                          <div key={bedingung.id} className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-medium text-gray-900 flex items-center">
                                <i className="ri-contract-line mr-2 text-blue-600"></i>
                                Bedingung {index + 1}
                              </h4>
                              <button
                                type="button"
                                onClick={() => removeVertragsbedingung(bedingung.id)}
                                className="text-red-600 hover:text-red-700 p-1"
                              >
                                <i className="ri-delete-bin-line"></i>
                              </button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <Input
                                label="Bedingung/Kategorie"
                                value={bedingung.bedingung}
                                onChange={e => updateVertragsbedingung(bedingung.id, 'bedingung', e.target.value)}
                                placeholder="z.B. Laufzeit, Garantie, Lieferzeit..."
                              />
                              <Input
                                label="Details/Wert"
                                value={bedingung.wert}
                                onChange={e => updateVertragsbedingung(bedingung.id, 'wert', e.target.value)}
                                placeholder="z.B. 3 Jahre, 24 Monate, 2-3 Wochen..."
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {currentData.vertragsbedingungen.length === 0 && (
                      <div className="text-center py-8 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="text-blue-400 mb-3">
                          <i className="ri-contract-line text-4xl"></i>
                        </div>
                        <h4 className="text-lg font-medium text-gray-700 mb-2">Keine Vertragsbedingungen hinzugefügt</h4>
                        <p className="text-gray-500 text-sm mb-4">
                          Fügen Sie strukturierte Vertragsbedingungen hinzu für eine professionelle Offerte.
                        </p>
                        <div className="text-xs text-gray-600 bg-white p-3 rounded-lg border max-w-md mx-auto">
                          <p className="font-medium mb-2">Beispiele für Vertragsbedingungen:</p>
                          <div className="grid grid-cols-2 gap-2 text-left">
                            <div><strong>Laufzeit:</strong> 3 Jahre</div>
                            <div><strong>Garantie:</strong> 24 Monate</div>
                            <div><strong>Lieferzeit:</strong> 2-3 Wochen</div>
                            <div><strong>Installation:</strong> Inklusive</div>
                            <div><strong>Wartung:</strong> Jährlich</div>
                            <div><strong>Kündigung:</strong> 3 Monate</div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-start">
                        <i className="ri-information-line text-blue-600 mr-2 mt-0.5"></i>
                        <div className="text-sm text-blue-800">
                          <p className="font-medium mb-1">Strukturierte Vertragsbedingungen:</p>
                          <p>• <strong>Links:</strong> Kategorie/Bedingung (z.B. "Laufzeit", "Garantie")</p>
                          <p>• <strong>Rechts:</strong> Konkreter Wert (z.B. "3 Jahre", "24 Monate")</p>
                          <p>• Erscheint als übersichtliche Tabelle in der PDF</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Zusammenfassung */}
                <div
                  className={`${
                    activeTab === 'rechnung' ? 'bg-blue-50' : activeTab === 'offerte' ? 'bg-green-50' : 'bg-orange-50'
                  } p-4 rounded-lg`}
                >
                  <h3 className="font-medium text-gray-900 mb-3">
                    {activeTab === 'rechnung' ? 'Rechnungssumme' : activeTab === 'offerte' ? 'Angebotssumme' : 'Auszahlungssumme'}
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Zwischensumme:</span>
                      <span>CHF {(zwischensumme || 0).toFixed(2)}</span>
                    </div>
                    {gesamtRabatt > 0 && (
                      <div className="flex justify-between text-orange-600">
                        <span>{activeTab === 'auszahlung' ? 'Abzüge:' : 'Gesamtrabatt:'}</span>
                        <span>-CHF {(gesamtRabatt || 0).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span>Nettobetrag:</span>
                      <span>CHF {(nettoBetrag || 0).toFixed(2)}</span>
                    </div>
                    {currentData.mehrwertsteuer > 0 && (
                      <div className="flex justify-between">
                        <span>MwSt. ({currentData.mehrwertsteuer}%):</span>
                        <span>CHF {(mwstBetrag || 0).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-medium text-lg border-t pt-2">
                      <span>{activeTab === 'rechnung' ? 'Gesamtbetrag:' : activeTab === 'offerte' ? 'Angebotssumme:' : 'Auszahlungsbetrag:'}</span>
                      <span>CHF {(bruttoBetrag || 0).toFixed(2)}</span>
                    </div>

                    {/* Rabatt Übersicht */}
                    {currentData.rabatte.length > 0 && (
                      <div className="mt-4 pt-3 border-t border-orange-200">
                        <h4 className="font-medium text-gray-700 mb-2 flex items-center">
                          <i className="ri-price-tag-3-line mr-2 text-orange-600"></i>
                          {activeTab === 'auszahlung' ? 'Angewandte Abzüge:' : 'Angewandte Rabatte:'}
                        </h4>
                        <div className="space-y-1">
                          {currentData.rabatte
                            .filter(r => r.beschreibung.trim())
                            .map((rabatt) => (
                              <div key={rabatt.id} className="flex justify-between text-xs text-orange-600">
                                <span>
                                  {rabatt.beschreibung} ({rabatt.typ === 'prozent' ? rabatt.wert + '%' : 'CHF ' + (Number(rabatt.wert) || 0).toFixed(2)}):
                                </span>
                                <span>-CHF {(Number(rabatt.betrag) || 0).toFixed(2)}</span>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                  </div>
                </div>

                {/* Aktionen */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <Button
                    type="button"
                    onClick={handleManualSave}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-medium"
                    disabled={generating || currentData.positionen.every(pos => !pos.beschreibung.trim())}
                  >
                    <i className="ri-save-line mr-2"></i>
                    Daten speichern
                  </Button>
                  <Button
                    type="button"
                    onClick={generatePDF}
                    className={`w-full ${
                      activeTab === 'rechnung'
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : activeTab === 'offerte'
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-orange-600 hover:bg-orange-700'
                    } text-white px-6 py-3 rounded-lg font-medium`}
                    disabled={generating || currentData.positionen.every(pos => !pos.beschreibung.trim())}
                  >
                    {generating ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Generiere PDF...
                      </>
                    ) : (
                      <>
                        <i className="ri-file-pdf-line mr-2"></i>
                        <span className="hidden sm:inline">
                          {activeTab === 'rechnung' ? 'Rechnung als PDF herunterladen' : 
                           activeTab === 'offerte' ? 'Offerte als PDF herunterladen' : 'Auszahlung als PDF herunterladen'}
                        </span>
                        <span className="sm:hidden">
                          {activeTab === 'rechnung' ? 'Rechnung PDF' : 
                           activeTab === 'offerte' ? 'Offerte PDF' : 'Auszahlung PDF'}
                        </span>
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* Status-Bestätigungsmodal */}
      <Modal
        isOpen={confirmationModal.isOpen}
        onClose={() => {}}
        title="📋 Status-Bestätigung erforderlich"
        maxWidth="md"
      >
        <div className="space-y-4">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-question-line text-2xl text-blue-600"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Wurde das Dokument wirklich versendet?
            </h3>
            <p className="text-gray-600 text-sm">
              Diese Bestätigung hilft bei der korrekten Nummernverwaltung
            </p>
          </div>

          {confirmationModal.invoiceData && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-2">Dokument-Details:</h4>
              <div className="text-sm space-y-1">
                <div>
                  <strong>Nummer:</strong> {confirmationModal.invoiceData.rechnungsNummer}
                </div>
                <div>
                  <strong>Kunde:</strong> {confirmationModal.invoiceData.kontakt || 'Kein Kunde'}
                </div>
                <div>
                  <strong>Betreff:</strong> {confirmationModal.invoiceData.betreff || 'Kein Betreff'}
                </div>
                <div>
                  <strong>Betrag:</strong> CHF {bruttoBetrag.toFixed(2)}
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex">
                <i className="ri-checkbox-circle-line text-green-600 mr-2 mt-0.5"></i>
                <div>
                  <h5 className="font-medium text-green-900">JA - Dokument wurde versendet</h5>
                  <p className="text-sm text-green-700 mt-1">
                    • Nummer wird als "versendet" markiert<br />
                    • Nächste Nummer wird generiert<br />
                    • Keine Wiederverwendung möglich
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
              <div className="flex">
                <i className="ri-close-circle-line text-orange-600 mr-2 mt-0.5"></i>
                <div>
                  <h5 className="font-medium text-orange-900">NEIN - Dokument nicht versendet</h5>
                  <p className="text-sm text-orange-700 mt-1">
                    • Nummer bleibt für Wiederverwendung verfügbar<br />
                    • Status bleibt auf "generiert"<br />
                    • PDF-Dialog öffnet sich erneut
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <Button
              onClick={() => handleConfirmationResponse(false)}
              className="flex-1 bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg font-medium"
            >
              <i className="ri-close-line mr-2"></i>
              NEIN - Nicht versendet
            </Button>
            <Button
              onClick={() => handleConfirmationResponse(true)}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium"
            >
              <i className="ri-check-line mr-2"></i>
              JA - Versendet
            </Button>
          </div>

          <div className="text-center">
            <p className="text-xs text-gray-500">
              💡 Diese Bestätigung verhindert doppelte Rechnungsnummern und hilft bei der Übersicht
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
}
